import * as actionTypes from 'js/constants/actionTypes';
import { SCHEMA_TYPE_AI_TOOLSET_EXPORTS } from 'js/schemas/aiTool';

export interface AIToolsetExport {
  id: number;
  export_id?: number; // Alias for id
  owner_id: number;
  org_id: number;
  tool_set_id: number; // References toolset directly (versionless)
  export_type: 'mcp_server' | 'copilot_connector' | 'sdk_bundle' | 'http_catalog';
  environment: 'dev' | 'stage' | 'prod';
  status: 'init' | 'paused' | 'active';
  public_name?: string | null;
  endpoint_url?: string | null;
  mcp_url?: string;
  export_metadata?: any | null;
  server_key?: string;
  // Cached tool definitions for performance
  tools_cache?: Record<string, any>;
  tools_cache_hash?: string;
  cache_generated_at?: string;
  // Flattened toolset fields (versionless)
  toolset_id?: number;
  toolset_name?: string;
  toolset_display_name?: string; // Deprecated alias
  toolset_description?: string;
  toolset_status?: 'init' | 'paused' | 'active';
  toolset_owner_id?: number | null;
  toolset_created_at?: string;
  toolset_updated_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AIToolsetExportsPagination {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  offset: number;
}

export interface AIToolsetExportsState {
  byId: Record<number, AIToolsetExport>;
  allIds: number[];
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
  pagination: AIToolsetExportsPagination | null;
}

const initialState: AIToolsetExportsState = {
  byId: {},
  allIds: [],
  loading: false,
  error: null,
  lastUpdated: null,
  pagination: null,
};

export default function aiToolsetExportsReducer(
  state: AIToolsetExportsState = initialState,
  action: any,
): AIToolsetExportsState {
  switch (action.type) {
    case actionTypes.GET_AI_TOOLSET_EXPORTS_REQUEST:
    case actionTypes.GET_AI_TOOLSET_EXPORT_REQUEST:
    case actionTypes.CREATE_AI_TOOLSET_EXPORT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.GET_AI_TOOLSET_EXPORTS_SUCCESS: {
      const exports = action.entities?.[SCHEMA_TYPE_AI_TOOLSET_EXPORTS] || {};
      const ids = action.result || [];
      const rspHeaders = action.rspHeaders || {};
      const shouldAppend = action.requestInfo?.append;

      // Extract pagination metadata from response headers
      const totalCount = Number(rspHeaders['X-Total-Count'] || 0);
      const currentPage = Number(rspHeaders['X-Current-Page'] || 1);
      const totalPages = Number(rspHeaders['X-Total-Page-Count'] || 1);
      const limit = Number(action.requestInfo?.limit || 50);
      const offset = Number(action.requestInfo?.offset || 0);

      // Ensure all exports have id field set from export_id
      const normalizedExports: Record<number, AIToolsetExport> = {};
      Object.keys(exports).forEach((key) => {
        const exp = exports[key];
        normalizedExports[key] = {
          ...exp,
          id: exp.export_id || exp.id,
        };
      });

      return {
        ...state,
        byId: { ...state.byId, ...normalizedExports },
        allIds: shouldAppend
          ? [...state.allIds, ...ids.filter((id) => !state.allIds.includes(id))]
          : ids,
        loading: false,
        lastUpdated: Date.now(),
        pagination: {
          totalCount,
          currentPage,
          totalPages,
          limit,
          offset,
        },
      };
    }

    case actionTypes.GET_AI_TOOLSET_EXPORT_SUCCESS:
    case actionTypes.CREATE_AI_TOOLSET_EXPORT_SUCCESS: {
      const exportData = action.response?.entities?.[SCHEMA_TYPE_AI_TOOLSET_EXPORTS] || {};
      const exportId = action.response?.result || action.requestInfo?.exportId;

      if (!exportId) return state;

      // Ensure export has id field set from export_id
      const normalizedExportData: Record<number, AIToolsetExport> = {};
      Object.keys(exportData).forEach((key) => {
        const exp = exportData[key];
        normalizedExportData[key] = {
          ...exp,
          id: exp.export_id || exp.id,
        };
      });

      return {
        ...state,
        byId: { ...state.byId, ...normalizedExportData },
        allIds: state.allIds.includes(exportId) ? state.allIds : [...state.allIds, exportId],
        loading: false,
        lastUpdated: Date.now(),
      };
    }

    case actionTypes.GET_AI_TOOLSET_EXPORTS_FAILURE:
    case actionTypes.GET_AI_TOOLSET_EXPORT_FAILURE:
    case actionTypes.CREATE_AI_TOOLSET_EXPORT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error?.message || 'An error occurred',
      };

    default:
      return state;
  }
}
